// Directory search for the owner picker — Microsoft Graph, called from the
// browser with the signed-in user's own token (see getGraphToken in auth.ts).
//
// Owners are directory-only (decided 2026-09-10): an initiative's DRI must be
// a real person from the company directory so their verified email is on the
// row — that's the handle the Slack integration resolves people by. There is
// no free-text "add a person" path anywhere in the app anymore.
//
// Nothing here touches our API. Picking a hit feeds the existing
// create-or-reuse POST /api/people with the directory name + email.

import { getGraphToken } from "./auth";
import { API_BASE, AUTH_ENABLED } from "./config";

export type DirectoryPerson = {
  id: string; // Entra object id — stable across renames
  name: string;
  email: string; // lowercase; always present (people without one are dropped)
  title: string | null;
};

export type DirectoryResult =
  // `truncated`: Graph returned the full page, so there may be more matches
  // than shown — the picker tells the user to keep typing.
  | { status: "ok"; people: DirectoryPerson[]; truncated: boolean }
  // The session needs one interactive consent step (see requestGraphConsent).
  | { status: "consent-needed" }
  // Auth off, not signed in, consent refused, or Graph refused the request.
  | { status: "unavailable" }
  | { status: "error"; message: string };

export type DirectorySearch = (query: string, signal?: AbortSignal) => Promise<DirectoryResult>;

export const GRAPH_USERS_URL = "https://graph.microsoft.com/v1.0/users";
export const MIN_QUERY = 2;
export const MAX_RESULTS = 8;

// Shape of a Graph user with the fields we $select. All of these are readable
// with the delegated User.ReadBasic.All permission. Note `accountEnabled` is
// NOT — filtering or selecting it needs User.Read.All and would 403 the whole
// request — so we don't ask for it; the check in mapGraphUser is defensive for
// any future scope widening.
export type GraphUser = {
  id: string;
  displayName?: string | null;
  mail?: string | null;
  userPrincipalName?: string | null;
  jobTitle?: string | null;
  accountEnabled?: boolean | null;
};

// OData string literals escape a single quote by doubling it (O'Brien → O''Brien).
export function escapeODataLiteral(s: string): string {
  return s.replace(/'/g, "''");
}

// Prefix match across the fields people actually type: first name, last name,
// full display name, or the start of an email. Plain startswith filters don't
// need the ConsistencyLevel/advanced-query dance that $search does.
export function buildUsersUrl(query: string, top = MAX_RESULTS): string {
  const q = escapeODataLiteral(query.trim());
  const fields = ["displayName", "givenName", "surname", "mail", "userPrincipalName"];
  const starts = fields.map(f => `startswith(${f},'${q}')`).join(" or ");
  const params = new URLSearchParams({
    $filter: starts,
    $select: "id,displayName,mail,userPrincipalName,jobTitle",
    $top: String(top),
  });
  return `${GRAPH_USERS_URL}?${params.toString()}`;
}

// Guest accounts carry a synthetic UPN (jane_agency.com#EXT#@tenant…) that is
// not a mailbox — only `mail` is real for them. Members usually have both.
function usableEmail(u: GraphUser): string | null {
  const mail = u.mail?.trim();
  if (mail && mail.includes("@")) return mail.toLowerCase();
  const upn = u.userPrincipalName?.trim();
  if (upn && upn.includes("@") && !upn.includes("#EXT#")) return upn.toLowerCase();
  return null;
}

// Null means "not a pickable owner": disabled account or no reachable email.
export function mapGraphUser(u: GraphUser): DirectoryPerson | null {
  if (u.accountEnabled === false) return null;
  const email = usableEmail(u);
  if (!email || !u.id) return null;
  const name = u.displayName?.trim() || email.split("@")[0];
  return { id: u.id, name, email, title: u.jobTitle?.trim() || null };
}

export function mapGraphUsers(users: GraphUser[]): DirectoryPerson[] {
  const seen = new Set<string>();
  const out: DirectoryPerson[] = [];
  for (const u of users) {
    const p = mapGraphUser(u);
    if (!p || seen.has(p.email)) continue;
    seen.add(p.email);
    out.push(p);
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

// One place for the HTTP → result mapping and the error shaping, shared by the
// real Graph call and the local mock. An aborted request is re-thrown so the
// caller's cancellation logic sees it; every other failure becomes a result.
async function fetchUsers(url: string, headers: Record<string, string>, signal?: AbortSignal): Promise<DirectoryResult> {
  try {
    const res = await fetch(url, { headers, signal });
    if (res.status === 401) return { status: "error", message: "Your directory session expired. Reload the page to sign in again." };
    if (res.status === 403) return { status: "unavailable" };
    if (res.status === 429) return { status: "error", message: "The directory is busy. Pause a moment, then keep typing." };
    if (!res.ok) return { status: "error", message: `Directory search failed (${res.status})` };
    const body = (await res.json()) as { value?: GraphUser[] };
    const raw = body.value ?? [];
    return { status: "ok", people: mapGraphUsers(raw), truncated: raw.length >= MAX_RESULTS };
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") throw err;
    return { status: "error", message: String((err as Error)?.message ?? err) };
  }
}

export const searchDirectory: DirectorySearch = async (query, signal) => {
  const q = query.trim();
  if (q.length < MIN_QUERY) return { status: "ok", people: [], truncated: false };

  // Local dev runs with auth off against mock-api.demo.js, which serves a
  // Graph-shaped directory so the picker can be exercised end to end. Dead
  // code in production builds (NODE_ENV is inlined by CRA).
  if (!AUTH_ENABLED && process.env.NODE_ENV === "development") {
    return fetchUsers(`${API_BASE}/directory?q=${encodeURIComponent(q)}`, {}, signal);
  }

  const token = await getGraphToken();
  if (token.status !== "ok") return { status: token.status };
  return fetchUsers(buildUsersUrl(q), { Authorization: `Bearer ${token.token}` }, signal);
};
